import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MusicNote, Sparkle, Waveform } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import { MusicGrid } from "@/components/MusicGrid";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GENRES = ["ambient", "meditation", "lo-fi", "cinematic", "spiritual", "jazz", "electronic", "classical"];
const MOODS = ["calm", "uplifting", "dark", "energetic", "dreamy", "nostalgic", "epic", "peaceful"];

export const MusicCreation = () => {
  const [tracks, setTracks] = useState([]);
  const [showNewTrack, setShowNewTrack] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [newTrack, setNewTrack] = useState({
    prompt: "",
    genre: "ambient",
    mood: "calm",
    duration: 60,
    bpm: null,
  });

  const loadTracks = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/music`);
      setTracks(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === "development") console.error("Error loading tracks:", error);
    }
  }, []);

  useEffect(() => {
    loadTracks();
  }, [loadTracks]);

  const generateTrack = async (e) => {
    e.preventDefault();
    if (!newTrack.prompt.trim()) return;
    setGenerating(true);
    try {
      const payload = { ...newTrack };
      if (!payload.bpm) delete payload.bpm;
      await axios.post(`${API}/music/generate`, payload);
      toast.success("Music track generated!");
      setNewTrack({ prompt: "", genre: "ambient", mood: "calm", duration: 60, bpm: null });
      setShowNewTrack(false);
      loadTracks();
    } catch (error) {
      if (error.response?.status === 429) {
        toast.error(error.response.data.detail || "Rate limit reached");
      } else {
        toast.error("Failed to generate track");
      }
    } finally {
      setGenerating(false);
    }
  };

  const deleteTrack = async (trackId) => {
    try {
      await axios.delete(`${API}/music/${trackId}`);
      toast.success("Track deleted");
      loadTracks();
    } catch (error) {
      toast.error("Failed to delete track");
    }
  };

  return (
    <div className="h-full flex flex-col bg-black" data-testid="music-creation-panel">
      {/* Header */}
      <div className="border-b border-border p-6 bg-surface">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Waveform size={28} weight="duotone" className="text-foreground" />
            <div>
              <h2 className="text-2xl font-bold tracking-tight" data-testid="music-header-title">
                AI Music Creation
              </h2>
              <p className="text-sm text-muted-foreground">
                Generate ambient, meditation & cinematic tracks
              </p>
            </div>
          </div>
          <Button
            onClick={() => setShowNewTrack(!showNewTrack)}
            className="bg-white text-black hover:bg-gray-200"
            data-testid="new-track-button"
          >
            <Sparkle size={20} weight="fill" className="mr-2" />
            Generate Track
          </Button>
        </div>

        {/* Generation Form */}
        <AnimatePresence>
          {showNewTrack && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-4 p-4 border border-border bg-surface"
              data-testid="new-track-form"
            >
              <form onSubmit={generateTrack} className="space-y-3">
                <Textarea
                  value={newTrack.prompt}
                  onChange={(e) => setNewTrack({ ...newTrack, prompt: e.target.value })}
                  placeholder="Describe the music you want... (e.g., Peaceful meditation music with Tibetan singing bowls and gentle rain)"
                  className="bg-transparent border-border min-h-[80px]"
                  data-testid="music-prompt-input"
                />
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Genre</label>
                    <select
                      value={newTrack.genre}
                      onChange={(e) => setNewTrack({ ...newTrack, genre: e.target.value })}
                      className="w-full bg-transparent border border-border p-2 text-sm"
                      data-testid="music-genre-select"
                    >
                      {GENRES.map((g) => (
                        <option key={g} value={g} className="bg-black">{g.charAt(0).toUpperCase() + g.slice(1)}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Mood</label>
                    <select
                      value={newTrack.mood}
                      onChange={(e) => setNewTrack({ ...newTrack, mood: e.target.value })}
                      className="w-full bg-transparent border border-border p-2 text-sm"
                      data-testid="music-mood-select"
                    >
                      {MOODS.map((m) => (
                        <option key={m} value={m} className="bg-black">{m.charAt(0).toUpperCase() + m.slice(1)}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Duration</label>
                    <select
                      value={newTrack.duration}
                      onChange={(e) => setNewTrack({ ...newTrack, duration: parseInt(e.target.value) })}
                      className="w-full bg-transparent border border-border p-2 text-sm"
                      data-testid="music-duration-select"
                    >
                      <option value={30} className="bg-black">30 seconds</option>
                      <option value={60} className="bg-black">1 minute</option>
                      <option value={120} className="bg-black">2 minutes</option>
                      <option value={300} className="bg-black">5 minutes</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">BPM (optional)</label>
                    <Input
                      type="number"
                      value={newTrack.bpm || ""}
                      onChange={(e) => setNewTrack({ ...newTrack, bpm: e.target.value ? parseInt(e.target.value) : null })}
                      placeholder="Auto"
                      className="bg-transparent border-border"
                      data-testid="music-bpm-input"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={generating}
                  className="w-full bg-white text-black hover:bg-gray-200"
                  data-testid="generate-track-button"
                >
                  {generating ? "Generating Track..." : "Generate Track"}
                </Button>
                <p className="text-[10px] text-yellow-600 text-center">
                  MOCK MODE - Music metadata is generated. Plug in Suno AI or another provider for actual audio.
                </p>
              </form>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Stats bar */}
        <div className="flex items-center gap-4 mt-4 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <MusicNote size={14} />
            {tracks.length} tracks
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <MusicGrid tracks={tracks} onDelete={deleteTrack} />
      </div>
    </div>
  );
};

export default MusicCreation;
